import PageTitle from '../components/PageTitle';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

// ── Constants ─────────────────────────────────────────────────────
const STEPS = ['Personal Info', 'ITR Type', 'Income Details', 'Documents', 'Review & Submit'];

const ITR_TYPES = [
  { type: 'ITR-1', desc: 'Salaried, pension. Income up to ₹50L.' },
  { type: 'ITR-2', desc: 'Capital gains, multiple properties.' },
  { type: 'ITR-3', desc: 'Business/profession income.' },
  { type: 'ITR-4', desc: 'Presumptive business (44AD/ADA).' },
  { type: 'ITR-5', desc: 'Firms, LLPs, AOPs.' },
  { type: 'ITR-6', desc: 'Companies.' },
];

const INIT = {
  fullName: '', pan: '', email: '', phone: '', dob: '', gender: 'M',
  residentialStatus: 'resident', aadhar: '', address: '',
  assessmentYr: '', itrType: '', filingType: 'original',
  annualIncome: '', taxPaid: '', deductions: '', salaryIncome: '',
  businessIncome: '', capitalGains: '', otherIncome: '', houseIncome: '',
  bankName: '', accountNo: '', ifsc: '', remarks: '',
};

const validators = {
  fullName:     v => !v.trim() ? 'Required' : '',
  pan:          v => !/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(v.trim()) ? 'Invalid PAN (e.g. ABCDE1234F)' : '',
  email:        v => !/^[\w.+-]+@[\w-]+\.[a-z]{2,}$/i.test(v.trim()) ? 'Invalid email' : '',
  phone:        v => !/^[6-9]\d{9}$/.test(v.trim()) ? 'Invalid 10-digit mobile' : '',
  address:      v => !v.trim() ? 'Required' : '',
  assessmentYr: v => !v ? 'Required' : '',
  itrType:      v => !v ? 'Select ITR type' : '',
  annualIncome: v => !v.trim() ? 'Required' : isNaN(Number(v)) ? 'Must be a number' : '',
};

const stepFields = {
  1: ['fullName', 'pan', 'email', 'phone', 'address'],
  2: ['assessmentYr', 'itrType'],
  3: ['annualIncome'],
  4: [], 5: [],
};

// ── Shared field components — defined at MODULE level, never inside a render ──

// Plain text / email / date input
function Field({ label, name, type = 'text', placeholder, hint, required, span2, value, onChange, error, ...rest }) {
  return (
    <div className={`field${span2 ? ' span2' : ''}`}>
      <label>{label}{required && <span className="req">*</span>}</label>
      <input
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={error ? 'err' : ''}
        {...rest}
      />
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="err-msg">⚠ {error}</span>}
    </div>
  );
}

// Select dropdown
function SelectField({ label, name, required, value, onChange, error, children }) {
  return (
    <div className="field">
      <label>{label}{required && <span className="req">*</span>}</label>
      <select name={name} value={value} onChange={onChange} className={error ? 'err' : ''}>
        {children}
      </select>
      {error && <span className="err-msg">⚠ {error}</span>}
    </div>
  );
}

// File upload zone
function FileZone({ label, name, hint, file, onChange }) {
  const hasFiles = Array.isArray(file) ? file.length > 0 : !!file;
  const filesList = Array.isArray(file) ? file : file ? [file] : [];

  return (
    <div className="field">
      <label>{label}</label>
      <div className={`file-zone ${hasFiles ? 'has-file' : ''}`}>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          onChange={e => onChange(name, Array.from(e.target.files))}
        />
        <div style={{ fontSize: '1.5rem', marginBottom: '.25rem' }}>{hasFiles ? '✅' : '📎'}</div>
        <div className="file-zone-label">
          {hasFiles ? `${filesList.length} file${filesList.length > 1 ? 's' : ''} selected` : 'Click to upload'}
        </div>
        {hasFiles ? (
          <div className="file-name" style={{ display: 'flex', flexDirection: 'column', gap: '.15rem', marginTop: '.25rem' }}>
            {filesList.map((f, i) => (
              <div key={i} style={{ fontSize: '.72rem', color: 'var(--navy)' }}>📄 {f.name}</div>
            ))}
          </div>
        ) : (
          <div className="file-zone-sub">{hint}</div>
        )}
      </div>
    </div>
  );
}

// ── Step components — all defined at MODULE level ──────────────────

function Step1({ data, errors, onChange }) {
  return (
    <>
      <div className="step-header">
        <div className="step-label">Step 1 of 5</div>
        <h2 className="step-title">Personal Information</h2>
        <p className="step-desc">Enter details exactly as on your PAN card.</p>
      </div>
      <div className="form-grid">
        <Field
          span2 name="fullName" label="Full Name" required
          placeholder="Rahul Kumar Sharma"
          value={data.fullName} onChange={onChange} error={errors.fullName}
        />
        <Field
          name="pan" label="PAN Number" required
          placeholder="ABCDE1234F" maxLength={10}
          hint="10-character Permanent Account Number"
          value={data.pan} onChange={onChange} error={errors.pan}
        />
        <Field
          name="email" label="Email Address" type="email" required
          placeholder="you@email.com"
          value={data.email} onChange={onChange} error={errors.email}
        />
        <Field
          name="phone" label="Mobile Number" required
          placeholder="9876543210" maxLength={10}
          hint="10-digit Indian mobile"
          value={data.phone} onChange={onChange} error={errors.phone}
        />
        <Field
          name="dob" label="Date of Birth" type="date" required
          value={data.dob} onChange={onChange} error={errors.dob}
        />
        <SelectField name="gender" label="Gender" value={data.gender} onChange={onChange}>
          <option value="M">Male</option>
          <option value="F">Female</option>
          <option value="O">Other</option>
        </SelectField>
        <SelectField name="residentialStatus" label="Residential Status" value={data.residentialStatus} onChange={onChange}>
          <option value="resident">Resident Indian</option>
          <option value="nri">NRI</option>
          <option value="rnor">RNOR</option>
        </SelectField>
        <Field
          name="aadhar" label="Aadhar Number"
          placeholder="1234 5678 9012" maxLength={14} hint="Optional"
          value={data.aadhar} onChange={onChange}
        />
        <Field
          span2 name="address" label="Address" required
          placeholder="Flat, Street, City, State, PIN"
          value={data.address} onChange={onChange} error={errors.address}
        />
      </div>
    </>
  );
}

function Step2({ data, errors, onChange }) {
  return (
    <>
      <div className="step-header">
        <div className="step-label">Step 2 of 5</div>
        <h2 className="step-title">ITR Type & Assessment Year</h2>
      </div>
      <div className="form-grid" style={{ marginBottom: '1.5rem' }}>
        <SelectField name="assessmentYr" label="Assessment Year" required
          value={data.assessmentYr} onChange={onChange} error={errors.assessmentYr}>
          <option value="">Select year...</option>
          {['2025-26', '2024-25', '2023-24', '2022-23'].map(y => (
            <option key={y} value={y}>AY {y}</option>
          ))}
        </SelectField>
        <SelectField name="filingType" label="Filing Type" value={data.filingType} onChange={onChange}>
          <option value="original">Original Return</option>
          <option value="revised">Revised Return</option>
          <option value="belated">Belated Return</option>
        </SelectField>
      </div>
      <div className="field">
        <label>Select ITR Form<span className="req">*</span></label>
        {errors.itrType && <span className="err-msg" style={{ marginBottom: '.5rem' }}>⚠ {errors.itrType}</span>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '.75rem', marginTop: '.35rem' }}>
          {ITR_TYPES.map(t => (
            <div key={t.type}
              onClick={() => onChange({ target: { name: 'itrType', value: t.type } })}
              style={{
                border: `2px solid ${data.itrType === t.type ? 'var(--navy)' : 'var(--border)'}`,
                background: data.itrType === t.type ? 'var(--bluel)' : 'var(--cream)',
                borderRadius: 10, padding: '.9rem', cursor: 'pointer', transition: 'all .2s',
              }}>
              <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: '.25rem' }}>{t.type}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--muted)' }}>{t.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Step3({ data, errors, onChange, files, onForm16Extracted, form16Status }) {
  const income     = parseFloat(data.annualIncome || 0);
  const deductions = parseFloat(data.deductions || 0);
  const taxPaid    = parseFloat(data.taxPaid || 0);
  const taxable    = Math.max(0, income - deductions);
  const tax = taxable <= 250000 ? 0
    : taxable <= 500000  ? (taxable - 250000) * 0.05
    : taxable <= 1000000 ? 12500 + (taxable - 500000) * 0.2
    : 112500 + (taxable - 1000000) * 0.3;
  const diff = taxPaid - tax;

  return (
    <>
      <div className="step-header">
        <div className="step-label">Step 3 of 5</div>
        <h2 className="step-title">Income & Tax Details</h2>
        <p className="step-desc">Enter details manually or upload Form 16 to auto-fill them.</p>
      </div>

      {/* ── Form 16 Auto-Extraction Card ── */}
      <div style={{ background: 'var(--bluel)', border: '1.5px dashed var(--blue)', borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '2rem' }}>⚡</span>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '.9rem', fontWeight: 700, color: 'var(--navy)', marginBottom: '.25rem' }}>Auto-Fill from Form 16</h3>
            <p style={{ fontSize: '.78rem', color: 'var(--muted)', marginBottom: '1rem' }}>
              Upload your Form 16 PDF to instantly extract and pre-fill your salary, tax, and deduction details below.
            </p>
            <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
              <input type="file" accept=".pdf" onChange={e => onForm16Extracted('docForm16', Array.from(e.target.files))} style={{ fontSize: '.78rem' }} />
              {form16Status === 'parsing' && <span className="spinner spinner-dark" style={{ width: 16, height: 16 }} />}
            </div>
            {form16Status === 'success' && (
              <div style={{ color: 'var(--green)', fontSize: '.78rem', fontWeight: 600, marginTop: '.5rem' }}>
                ✅ Income and tax details successfully pre-filled! Please review them below.
              </div>
            )}
            {form16Status === 'empty' && (
              <div style={{ color: '#E65100', fontSize: '.78rem', fontWeight: 600, marginTop: '.5rem' }}>
                ℹ️ Could not automatically extract details. Please enter them manually below.
              </div>
            )}
            {files?.docForm16 && files.docForm16.length > 0 && (
              <div style={{ fontSize: '.75rem', color: 'var(--navy)', marginTop: '.5rem', fontWeight: 500 }}>
                📄 Attached: {files.docForm16[0].name}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="form-grid">
        <Field
          name="annualIncome" label="Gross Annual Income (₹)" required
          placeholder="750000"
          value={data.annualIncome} onChange={onChange} error={errors.annualIncome}
        />
        <Field
          name="taxPaid" label="TDS / Advance Tax Paid (₹)"
          placeholder="85000"
          value={data.taxPaid} onChange={onChange}
        />
        <Field
          name="deductions" label="Total Deductions (₹)"
          placeholder="150000" hint="80C + 80D + HRA etc."
          value={data.deductions} onChange={onChange}
        />
        <Field
          name="salaryIncome" label="Salary Income (₹)"
          placeholder="600000"
          value={data.salaryIncome} onChange={onChange}
        />
        <Field
          name="businessIncome" label="Business / Profession Income (₹)"
          placeholder="0"
          value={data.businessIncome} onChange={onChange}
        />
        <Field
          name="capitalGains" label="Capital Gains (₹)"
          placeholder="0" hint="STCG + LTCG"
          value={data.capitalGains} onChange={onChange}
        />
        <Field
          name="otherIncome" label="Other Income (₹)"
          placeholder="0"
          value={data.otherIncome} onChange={onChange}
        />
        <Field
          name="houseIncome" label="House Property Income (₹)"
          placeholder="0"
          value={data.houseIncome} onChange={onChange}
        />
      </div>

      {income > 0 && (
        <div style={{ background: 'var(--navy)', borderRadius: 12, padding: '1.25rem 1.5rem', marginTop: '1.5rem' }}>
          <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--goldt)', fontWeight: 600, marginBottom: '1rem' }}>
            Quick Tax Estimate
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', textAlign: 'center' }}>
            <div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', color: 'var(--goldt)' }}>
                ₹{taxable.toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: '.7rem', color: 'rgba(255,255,255,.4)', marginTop: 2 }}>Taxable Income</div>
            </div>
            <div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', color: 'var(--goldt)' }}>
                ₹{Math.round(tax).toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: '.7rem', color: 'rgba(255,255,255,.4)', marginTop: 2 }}>Est. Tax Liability</div>
            </div>
            <div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', color: diff >= 0 ? '#5DCAA5' : '#F09595' }}>
                {diff >= 0 ? '▲' : '▼'} ₹{Math.abs(Math.round(diff)).toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: '.7rem', color: 'rgba(255,255,255,.4)', marginTop: 2 }}>
                {diff >= 0 ? 'Refund Due' : 'Tax Payable'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '1.75rem', paddingTop: '1.5rem', borderTop: '1px solid var(--creamd)' }}>
        <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--navy)', marginBottom: '1rem' }}>
          🏦 Bank Details (for refund)
        </div>
        <div className="form-grid three">
          <Field
            name="bankName" label="Bank Name"
            placeholder="State Bank of India"
            value={data.bankName} onChange={onChange}
          />
          <Field
            name="accountNo" label="Account Number"
            placeholder="00000012345678"
            value={data.accountNo} onChange={onChange}
          />
          <Field
            name="ifsc" label="IFSC Code"
            placeholder="SBIN0001234" maxLength={11}
            value={data.ifsc} onChange={onChange}
          />
        </div>
      </div>
    </>
  );
}

function Step4({ files, handleFile, data, onChange, onForm16Extracted, form16Status }) {
  const docs = [
    { name: 'docForm16',      label: 'Form 16',             hint: 'Salary certificate from employer — auto-extracts income details!' },
    { name: 'docAis',         label: 'AIS / 26AS',          hint: 'Download from income tax portal' },
    { name: 'docBank',        label: 'Bank Statement',      hint: 'April–March for the FY' },
    { name: 'docInvestments', label: 'Investment Proofs',   hint: 'LIC, PPF, ELSS, 80D receipts' },
    { name: 'docHomeloan',    label: 'Home Loan Certificate',hint: 'Interest certificate from bank' },
    { name: 'docOther',       label: 'Other Documents',     hint: 'Capital gains, rent receipts etc.' },
  ];
  return (
    <>
      <div className="step-header">
        <div className="step-label">Step 4 of 5</div>
        <h2 className="step-title">Upload Documents</h2>
        <p className="step-desc">PDF, JPG, PNG accepted. Max 5MB per file.</p>
      </div>

      {/* Form 16 extraction status */}
      {form16Status === 'parsing' && (
        <div className="alert alert-info" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <span className="spinner spinner-dark" style={{ width: 16, height: 16 }} /> Extracting income details from your Form 16...
        </div>
      )}
      {form16Status === 'success' && (
        <div style={{ marginBottom: '1rem', background: 'var(--greenl)', border: '1px solid var(--green)', borderRadius: 10, padding: '.9rem 1.1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.3rem' }}>
            <span style={{ fontSize: '1.2rem' }}>✅</span>
            <span style={{ fontWeight: 600, color: 'var(--green)', fontSize: '.88rem' }}>Income details extracted from Form 16!</span>
          </div>
          <div style={{ fontSize: '.8rem', color: 'var(--muted)' }}>
            Fields like Gross Salary, TDS, and Deductions have been pre-filled in Step 3. You can go back to review and edit them.
          </div>
        </div>
      )}
      {form16Status === 'empty' && (
        <div className="alert alert-info" style={{ marginBottom: '1rem', fontSize: '.82rem' }}>
          ℹ️ Could not extract details from this Form 16 (may be scanned or non-standard). Please enter income details manually in Step 3.
        </div>
      )}

      <div className="form-grid">
        {docs.map(d => (
          <FileZone key={d.name} name={d.name} label={d.label} hint={d.hint}
            file={files[d.name]} onChange={d.name === 'docForm16' ? onForm16Extracted : handleFile} />
        ))}
      </div>
      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Additional Remarks</label>
        <textarea
          name="remarks" rows={3}
          placeholder="Any special instructions for our CA team..."
          value={data.remarks}
          onChange={onChange}
        />
      </div>
    </>
  );
}

function Step5({ data, files }) {
  const Row = ({ label, value }) => value ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '.45rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.85rem' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  ) : null;

  return (
    <>
      <div className="step-header">
        <div className="step-label">Step 5 of 5</div>
        <h2 className="step-title">Review & Submit</h2>
        <p className="step-desc">Please review all details before submitting.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.75rem' }}>Personal Info</div>
          <Row label="Name"   value={data.fullName} />
          <Row label="PAN"    value={data.pan} />
          <Row label="Email"  value={data.email} />
          <Row label="Phone"  value={data.phone} />
          <Row label="Address" value={data.address} />
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.75rem' }}>Filing Details</div>
          <Row label="Assessment Year" value={`AY ${data.assessmentYr}`} />
          <Row label="ITR Type"        value={data.itrType} />
          <Row label="Annual Income"   value={`₹${Number(data.annualIncome || 0).toLocaleString('en-IN')}`} />
          <Row label="TDS Paid"        value={data.taxPaid ? `₹${Number(data.taxPaid).toLocaleString('en-IN')}` : null} />
          <Row label="Bank"            value={data.bankName} />
          <Row label="IFSC"            value={data.ifsc} />
        </div>
      </div>
      <div style={{ marginTop: '1.5rem' }}>
        <div style={{ fontWeight: 600, fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--gold)', marginBottom: '.75rem' }}>Documents</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
          {Object.entries(files).filter(([, v]) => v && (Array.isArray(v) ? v.length > 0 : true)).flatMap(([k, v]) => {
            const list = Array.isArray(v) ? v : [v];
            return list.map((f, idx) => (
              <span key={`${k}-${idx}`} className="badge badge-filed">📄 {f.name}</span>
            ));
          })}
          {Object.values(files).flatMap(v => Array.isArray(v) ? v : v ? [v] : []).length === 0 && (
            <span style={{ color: 'var(--muted)', fontSize: '.85rem' }}>No documents uploaded</span>
          )}
        </div>
      </div>
      <div className="alert alert-info" style={{ marginTop: '1.5rem', fontSize: '.83rem' }}>
        ⚠️ By submitting, you authorise MyTaxNiti (Authorised ERI) to e-file your ITR with the Income Tax Department on your behalf.
      </div>
    </>
  );
}

function SuccessScreen({ filing, onDone }) {
  return (
    <div className="page">
      <div className="card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
        <div style={{ width: 80, height: 80, background: 'var(--greenl)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '2rem' }}>✅</div>
        <h2 style={{ color: 'var(--navy)', marginBottom: '.5rem' }}>Filing Submitted!</h2>
        <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>Your ITR has been received by our CA team.</p>
        <div style={{ display: 'inline-block', background: 'var(--bluel)', color: 'var(--blue)', padding: '.5rem 1.5rem', borderRadius: 8, fontWeight: 700, marginBottom: '1.5rem' }}>
          Filing ID: #{filing.id}
        </div>
        <div style={{ background: 'var(--cream)', borderRadius: 12, padding: '1.25rem', maxWidth: 420, margin: '0 auto 2rem', textAlign: 'left' }}>
          <div style={{ fontSize: '.78rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--navy)', marginBottom: '.75rem' }}>What happens next</div>
          {['CA reviews your documents (24 hrs)', 'Draft ITR sent to your email', 'Your approval required', 'We e-file with Income Tax Dept.', 'ITR-V acknowledgement sent to you']
            .map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: '.75rem', marginBottom: '.6rem', fontSize: '.85rem', color: 'var(--muted)', alignItems: 'flex-start' }}>
                <div style={{ width: 22, height: 22, background: 'var(--navy)', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                {s}
              </div>
            ))}
        </div>
        <button className="btn btn-primary" onClick={onDone}>View My Applications →</button>
      </div>
    </div>
  );
}

// ── Main FilingForm component ──────────────────────────────────────
export default function FilingForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep]       = useState(1);
  const [data, setData]       = useState({
    ...INIT,
    fullName: user?.fullName || '',
    email:    user?.email    || '',
    phone:    user?.phone    || '',
    pan:      user?.pan      || '',
  });
  const [files, setFiles]     = useState({});
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(null);
  const [form16Status, setForm16Status] = useState(null); // null | 'parsing' | 'success' | 'empty' | 'error'

  const handle = e => {
    const { name, value } = e.target;
    const v = name === 'pan' || name === 'ifsc' ? value.toUpperCase() : value;
    setData(d => ({ ...d, [name]: v }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const handleFile = (name, filesList) => {
    if (filesList && filesList.length > 0) {
      for (let f of filesList) {
        if (f.size > 5 * 1024 * 1024) { alert('Max file size is 5MB per file'); return; }
      }
    }
    setFiles(f => ({ ...f, [name]: filesList }));
  };

  // ── Form 16 upload with auto-extraction ──────────────────────
  const handleForm16Upload = async (name, filesList) => {
    handleFile(name, filesList);
    const file = filesList && filesList[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return;
    setForm16Status('parsing');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: extracted } = await api.post('/api/filing/parse-form16', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Filter out internal keys (prefixed with _)
      const fields = Object.entries(extracted).filter(([k]) => !k.startsWith('_'));
      if (fields.length > 0) {
        // Merge extracted values — only overwrite empty fields
        setData(prev => {
          const merged = { ...prev };
          fields.forEach(([k, v]) => {
            if (!merged[k] || merged[k] === '' || merged[k] === '0') merged[k] = v;
          });
          return merged;
        });
        setForm16Status('success');
      } else {
        setForm16Status('empty');
      }
    } catch (err) {
      console.error('Form 16 parse failed:', err);
      setForm16Status('empty');
    }
  };

  const validateStep = () => {
    const fields = stepFields[step] || [];
    const errs = {};
    fields.forEach(f => { const e = validators[f]?.(data[f] || ''); if (e) errs[f] = e; });
    setErrors(e => ({ ...e, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const next = () => { if (validateStep()) { setStep(s => s + 1); window.scrollTo(0, 0); } };
  const prev = () => { setStep(s => s - 1); window.scrollTo(0, 0); };

  const submit = async () => {
    setLoading(true); setApiError('');
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => { if (v) fd.append(k, v); });
      Object.entries(files).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          v.forEach(file => fd.append(k, file));
        } else if (v) {
          fd.append(k, v);
        }
      });
      const { data: res } = await api.post('/api/filing/submit', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(res);
    } catch (err) {
      setApiError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally { setLoading(false); }
  };

  if (success) return <SuccessScreen filing={success} onDone={() => navigate('/applications')} />;

  return (
    <>
      <div className="page-hero">
        <div className="page-hero-label">ITR Filing</div>
        <h1>File Income Tax Return</h1>
        <p>Complete in 5 simple steps — our CA reviews within 24 hours</p>
      </div>

      {/* Progress bar */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '1.5rem 2rem' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div className="progress-track">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const state = n < step ? 'done' : n === step ? 'active' : 'pending';
              return (
                <div key={label} className={`pt-step ${state}`}>
                  <div className="pt-circle">{state === 'done' ? '✓' : n}</div>
                  <div className="pt-label">{label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="page">
        <div className="card">
          {apiError && <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>⚠ {apiError}</div>}

          {step === 1 && <Step1 data={data} errors={errors} onChange={handle} />}
          {step === 2 && <Step2 data={data} errors={errors} onChange={handle} />}
          {step === 3 && <Step3 data={data} errors={errors} onChange={handle}
            files={files} onForm16Extracted={handleForm16Upload} form16Status={form16Status} />}
          {step === 4 && <Step4 data={data} files={files} handleFile={handleFile} onChange={handle}
            onForm16Extracted={handleForm16Upload} form16Status={form16Status} />}
          {step === 5 && <Step5 data={data} files={files} />}

          <div className="btn-row">
            {step > 1
              ? <button className="btn btn-outline" onClick={prev}>← Back</button>
              : <span />
            }
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>Step {step} of 5</span>
              {step < 5
                ? <button className="btn btn-primary" onClick={next}>Continue →</button>
                : <button className="btn btn-gold" onClick={submit} disabled={loading}>
                    {loading ? <><span className="spinner" /> Submitting...</> : '🚀 Submit Filing'}
                  </button>
              }
            </div>
          </div>
        </div>
      </div>
    </>
  );
}